// `/dancers/$talentId` — one Dancer's record, and the one place two contexts
// meet: Roster's pane takes Talent Profile's editor as an opaque section. The
// name is saved by hand, the bio saves itself, and neither context knows.

import { useQueryClient } from '@tanstack/react-query';
import {
	createFileRoute,
	useNavigate,
	useRouteContext,
} from '@tanstack/react-router';
import { DancerProfile } from '~/bounded-contexts/roster/ui/dancer-profile/dancer-profile';
import {
	DancerProfileLoading,
	DancerProfileNotFound,
} from '~/bounded-contexts/roster/ui/dancer-profile/dancer-profile-fallback';
import { rosterQueryKey } from '~/bounded-contexts/roster/ui/dancers-page/query-key';
import { useDancer } from '~/bounded-contexts/roster/ui/dancers-page/use-dancer';
import { BioEditor } from '~/bounded-contexts/talent-profile/ui/bio-editor/bio-editor';
import { useBioDraft } from '~/bounded-contexts/talent-profile/ui/bio-editor/use-bio-draft';
import { BioStatus } from '~/bounded-contexts/talent-profile/ui/bio-status/bio-status';
import { PublishControl } from '~/bounded-contexts/talent-profile/ui/publication/publish-control';

function DancerProfileRouteComponent() {
	const { talentId } = Route.useParams();
	const ctx = useRouteContext({ from: '/_authenticated' });
	const club_id = ctx?.scope?.club?.id ?? '';
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const dancer = useDancer(club_id, talentId);
	const bio = useBioDraft(club_id, talentId);

	const back_to_roster = () => void navigate({ to: '/dancers' });

	if (dancer.is_pending) return <DancerProfileLoading />;
	if (!dancer.talent) return <DancerProfileNotFound on_back={back_to_roster} />;

	const is_archived = dancer.talent.archivedAt !== null;

	return (
		<DancerProfile
			key={dancer.talent.id}
			talent={dancer.talent}
			bio={{
				node: (
					<BioEditor
						is_loading={bio.is_pending}
						initial_bio={bio.initial_bio}
						remaining={bio.remaining}
						save_state={bio.save_state}
						error={bio.save_error}
						disabled={is_archived}
						on_change={bio.set_bio}
					/>
				),
				status: (
					<BioStatus
						published_at={bio.published_at}
						updated_at={bio.updated_at}
					/>
				),
				publish: (
					<PublishControl
						is_published={bio.is_published}
						is_pending={bio.is_publishing}
						disabled={bio.is_pending}
						error={bio.publication_error}
						on_change={bio.set_published}
					/>
				),
				is_dirty: bio.is_dirty,
			}}
			on_back={back_to_roster}
			on_changed={() => {
				// She crosses between the working roster and the archive, so both
				// lists are stale after any of these writes.
				for (const archived of [false, true]) {
					void queryClient.invalidateQueries({
						queryKey: rosterQueryKey(club_id, archived),
					});
				}
			}}
		/>
	);
}

export const Route = createFileRoute('/_authenticated/dancers/$talentId')({
	component: DancerProfileRouteComponent,
});
